/**
 * Transfer Plan Service
 * 
 * Service for handling transfer plan-related API calls.
 */

import { apiConfig } from "@/config/api";
import { apiFetch, HEAVY_API_TIMEOUT_MS, UPLOAD_API_TIMEOUT_MS } from "@/lib/apiFetch";
import {
  parseFlatTransactions,
  type FlatTransactionsResponse,
} from "@/services/transferPlan/flat";

export interface TransferPlanBatch {
  batchName: string;
  payments: MatchedPayment[];
  totalAmount: number;
}

export interface MatchedPayment {
  csvRow: {
    order: string;
    settlementTime: string;
    amount: number;
    transactionFee: number;
    transactionId: string;
    customerEmail: string;
  };
  paymentRequest: {
    _id: string;
    status: string;
    province?: string;
    kabkot?: string;
    user?: {
      _id: string;
      callsign: string;
      nama_lengkap: string;
      email: string;
    };
  };
  batchName?: string;
}

export interface UnmatchedOrder {
  order: string;
  customerEmail: string;
  amount: number;
  settlementTime: string;
}

export interface TransferPlanResult {
  batch: TransferPlanBatch;
  matchedPayments: MatchedPayment[];
  unmatchedOrders: UnmatchedOrder[];
  summary: {
    totalRows: number;
    matchedCount: number;
    unmatchedCount: number;
    totalAmount: number;
  };
  planId: string;
}

class TransferPlanService {
  /**
   * Upload CSV file and process it
   * @param file - CSV file to upload
   * @returns Promise with transfer plan result
   */
  async uploadCsvFile(file: File): Promise<TransferPlanResult> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiFetch(
        `${apiConfig.baseURL}/api/payment/transfer-plan/upload`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
          timeoutMs: UPLOAD_API_TIMEOUT_MS,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data as TransferPlanResult;
    } catch (error) {
      console.error("Error uploading CSV file:", error);
      throw error;
    }
  }

  /**
   * Download transfer plan as CSV
   * @param planData - Transfer plan data to download
   * @returns Promise that resolves when download starts
   */
  async downloadTransferPlan(planData: TransferPlanResult): Promise<void> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/payment/transfer-plan/download`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ planData }),
          timeoutMs: HEAVY_API_TIMEOUT_MS,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      // Get the blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transfer-plan-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading transfer plan:", error);
      throw error;
    }
  }

  /**
   * Confirm transfer plan and update PaymentRequests
   * @param planData - Transfer plan data to confirm
   * @returns Promise with confirmation result
   */
  async confirmTransferPlan(planData: TransferPlanResult): Promise<{
    success: boolean;
    message: string;
    updatedCount: number;
    transferPlanId?: string;
  }> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/payment/transfer-plan/confirm`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ planData }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error confirming transfer plan:", error);
      throw error;
    }
  }

  /**
   * Get all transfer plans
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   * @param sortBy - Sort field (default: "createdAt")
   * @param sortOrder - Sort order (default: "desc")
   * @returns Promise with paginated transfer plans
   */
  async getAllTransferPlans(
    page: number = 1,
    limit: number = 20,
    sortBy: "createdAt" | "batchName" = "createdAt",
    sortOrder: "asc" | "desc" = "desc"
  ): Promise<{
    data: Array<{
      _id: string;
      batchName: string;
      planId: string;
      summary: {
        totalRows: number;
        matchedCount: number;
        unmatchedCount: number;
        totalAmount: number;
      };
      totalWimaxIncome: number;
      totalOrpusIncome: number;
      totalOrdaIncome: number;
      totalOrlokIncome: number;
      matchedLocation: boolean;
      createdBy?: {
        _id: string;
        callsign?: string;
        nama_lengkap?: string;
        email?: string;
      };
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder,
      });

      const response = await apiFetch(
        `${apiConfig.baseURL}/api/payment/transfer-plans?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching transfer plans:", error);
      throw error;
    }
  }

  /**
   * Get a single transfer plan by ID
   * @param planId - Transfer plan ID
   * @returns Promise with transfer plan data
   */
  async getTransferPlanById(planId: string): Promise<{
    _id: string;
    batchName: string;
    planId: string;
    summary: {
      totalRows: number;
      matchedCount: number;
      unmatchedCount: number;
      totalAmount: number;
    };
    totalWimaxIncome: number;
    totalOrpusIncome: number;
    totalOrdaIncome: number;
    totalOrlokIncome: number;
    matchedLocation: boolean;
    createdBy?: {
      _id: string;
      callsign?: string;
      nama_lengkap?: string;
      email?: string;
    };
    createdAt: string;
    updatedAt: string;
  }> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/payment/transfer-plans/${planId}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching transfer plan:", error);
      throw error;
    }
  }

  /**
   * Get location aggregation for a transfer plan
   * @param planId - Transfer plan ID
   * @returns Promise with location aggregation data
   */
  async getLocationAggregation(planId: string): Promise<{
    cities: Array<{
      province: string;
      kabkot: string;
      count: number;
      totalAmount: number;
      totalWimaxIncome: number;
      totalOrpusIncome: number;
      totalOrdaIncome: number;
      totalOrlokIncome: number;
      bank?: string;
      accountNumber?: string;
      accountName?: string;
    }>;
    totalPayments: number;
    totalAmount: number;
    totalWimaxIncome: number;
    totalOrpusIncome: number;
    totalOrdaIncome: number;
    totalOrlokIncome: number;
  }> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/payment/transfer-plans/${planId}/location-aggregation`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching location aggregation:", error);
      throw error;
    }
  }

  /**
   * Get detailed transactions for a transfer plan
   * @param planId - Transfer plan ID
   * @returns Promise with transaction data
   */
  async getTransferPlanTransactions(
    planId: string,
    options?: { flat?: boolean }
  ): Promise<{
    transactions: Array<{
      _id: string;
      paymentId?: string;
      status: string;
      province?: string;
      kabkot?: string;
      wimaxIncome?: number;
      orpusIncome?: number;
      ordaIncome?: number;
      orlokIncome?: number;
      batchName?: string;
      user?: {
        _id: string;
        callsign?: string;
        nama_lengkap?: string;
        email?: string;
      };
      paymentGatewayData?: any;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
  }> {
    try {
      const qs = options?.flat ? "?flat=1" : "";
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/payment/transfer-plans/${planId}/transactions${qs}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (options?.flat) {
        return parseFlatTransactions(data as FlatTransactionsResponse);
      }

      return data;
    } catch (error) {
      console.error("Error fetching transfer plan transactions:", error);
      throw error;
    }
  }

  /**
   * Delete a transfer plan
   * Automatically removes batchName and income fields from associated PaymentRequests
   * @param planId - Transfer plan ID
   * @returns Promise with deletion result
   */
  async deleteTransferPlan(planId: string): Promise<{
    message: string;
    updatedPaymentsCount: number;
  }> {
    try {
      const url = `${apiConfig.baseURL}/api/payment/transfer-plans/${planId}`;

      const response = await apiFetch(url, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error deleting transfer plan:", error);
      throw error;
    }
  }
}

export const transferPlanService = new TransferPlanService();

